import { PagePaths } from '@revelio/core';

import type { Page } from '../components/PageConfigProvider/types';
import { activityComposition } from '../templates/activity/activity-composition';
import { activityMarketConditions } from '../templates/activity/activity-market-conditions';
import { activityPay } from '../templates/activity/activity-pay';
import { activityPostings } from '../templates/activity/activity-postings';
import { companyComposition } from '../templates/company/company-composition';
import { companyDiversity } from '../templates/company/company-diversity';
import { companyOverview } from '../templates/company/company-overview';
import { companyPay } from '../templates/company/company-pay';
import { companyPostings } from '../templates/company/company-postings';
import { companyScreener } from '../templates/company/company-screener';
import { companySentiment } from '../templates/company/company-sentiment';
import { companySubsidiaries } from '../templates/company/company-subsidiaries';
import { companyTransitions } from '../templates/company/company-transitions';
import { postingDiscovery } from '../templates/discovery/posting-discovery';
import { talentDiscovery } from '../templates/discovery/talent-discovery';
import { geographyComposition } from '../templates/geo/geography-composition';
import { geographyMarketConditions } from '../templates/geo/geography-market-conditions';
import { geographyOverview } from '../templates/geo/geography-overview';
import { geographyPay } from '../templates/geo/geography-pay';
import { geographyPostings } from '../templates/geo/geography-postings';
import { geographyScreener } from '../templates/geo/geography-screener';
import { geographySentiment } from '../templates/geo/geography-sentiment';
import { geographyTransitions } from '../templates/geo/geography-transitions';
import { industryComposition } from '../templates/industry/industry-composition';
import { industryMarketConditions } from '../templates/industry/industry-market-conditions';
import { industryPay } from '../templates/industry/industry-pay';
import { industryPostings } from '../templates/industry/industry-postings';
import { industrySentiment } from '../templates/industry/industry-sentiment';
import { industryTransitions } from '../templates/industry/industry-transitions';
import { getAllReportTemplates } from '../templates/reports/report-templates-registry';
import { roleComposition } from '../templates/role/role-composition';
import { roleMarketConditions } from '../templates/role/role-market-conditions';
import { roleOverview } from '../templates/role/role-overview';
import { rolePay } from '../templates/role/role-pay';
import { rolePostings } from '../templates/role/role-postings';
import { roleScreener } from '../templates/role/role-screener';
import { roleSentiment } from '../templates/role/role-sentiment';
import { roleTransitions } from '../templates/role/role-transitions';
import { skillComposition } from '../templates/skill/skill-composition';
import { skillMarketConditions } from '../templates/skill/skill-market-conditions';
import { skillPay } from '../templates/skill/skill-pay';
import { skillPostings } from '../templates/skill/skill-postings';

const baseTemplates: Page[] = [
  companyOverview,
  geographyOverview,
  roleOverview,
  roleComposition,
  companySubsidiaries,

  companyComposition,
  industryComposition,
  geographyComposition,
  skillComposition,
  activityComposition,

  companyTransitions,
  industryTransitions,
  geographyTransitions,
  roleTransitions,

  companyPostings,
  industryPostings,
  geographyPostings,
  rolePostings,
  skillPostings,
  activityPostings,

  companyPay,
  industryPay,
  geographyPay,
  rolePay,
  skillPay,
  activityPay,

  industryMarketConditions,
  geographyMarketConditions,
  roleMarketConditions,

  companyDiversity,

  companySentiment,
  industrySentiment,
  geographySentiment,
  roleSentiment,
  skillMarketConditions,
  activityMarketConditions,

  companyScreener,
  geographyScreener,
  roleScreener,

  talentDiscovery,
  postingDiscovery,
];

export const getLocalTemplates = (): Page[] => [
  ...baseTemplates,
  ...getAllReportTemplates(),
];

export const getLocalTemplate = (pageId: string): Page | undefined => {
  const localTemplate = getLocalTemplates().find((t) => t.id === pageId);
  return localTemplate;
};

const PathToPageConfigLookup = {
  [PagePaths.COMPANY_COMPOSITIONS]: companyComposition,
  [PagePaths.COMPANY_SUMMARY]: companyOverview,
  [PagePaths.COMPANY_PAY]: companyPay,
  [PagePaths.COMPANY_POSTINGS]: companyPostings,
  [PagePaths.COMPANY_DIVERSITY]: companyDiversity,
  [PagePaths.COMPANY_SENTIMENT]: companySentiment,
  [PagePaths.COMPANY_SUBSIDIARIES]: companySubsidiaries,
  [PagePaths.COMPANY_TRANSITIONS]: companyTransitions,

  [PagePaths.INDUSTRY_COMPOSITIONS]: industryComposition,
  [PagePaths.INDUSTRY_PAY]: industryPay,
  [PagePaths.INDUSTRY_MARKET_CONDITIONS]: industryMarketConditions,
  [PagePaths.INDUSTRY_POSTINGS]: industryPostings,
  [PagePaths.INDUSTRY_SENTIMENT]: industrySentiment,
  [PagePaths.INDUSTRY_TRANSITIONS]: industryTransitions,

  [PagePaths.GEOGRAPHY_COMPOSITIONS]: geographyComposition,
  [PagePaths.GEOGRAPHY_SUMMARY]: geographyOverview,
  [PagePaths.GEOGRAPHY_PAY]: geographyPay,
  [PagePaths.GEOGRAPHY_POSTINGS]: geographyPostings,
  [PagePaths.GEOGRAPHY_MARKET_CONDITIONS]: geographyMarketConditions,
  [PagePaths.GEOGRAPHY_SENTIMENT]: geographySentiment,
  [PagePaths.GEOGRAPHY_TRANSITIONS]: geographyTransitions,

  [PagePaths.ROLE_COMPOSITIONS]: roleComposition,
  [PagePaths.ROLE_SUMMARY]: roleOverview,
  [PagePaths.ROLE_PAY]: rolePay,
  [PagePaths.ROLE_POSTINGS]: rolePostings,
  [PagePaths.ROLE_MARKET_CONDITIONS]: roleMarketConditions,
  [PagePaths.ROLE_SENTIMENT]: roleSentiment,
  [PagePaths.ROLE_TRANSITIONS]: roleTransitions,

  [PagePaths.SKILL_COMPOSITIONS]: skillComposition,
  [PagePaths.SKILL_PAY]: skillPay,
  [PagePaths.SKILL_POSTINGS]: skillPostings,
  [PagePaths.SKILL_MARKET_CONDITIONS]: skillMarketConditions,
  [PagePaths.ACTIVITY_COMPOSITIONS]: activityComposition,
  [PagePaths.ACTIVITY_PAY]: activityPay,
  [PagePaths.ACTIVITY_POSTINGS]: activityPostings,
  [PagePaths.ACTIVITY_MARKET_CONDITIONS]: activityMarketConditions,
} as const satisfies Partial<Record<PagePaths, Page>>;

export type PublicCustomDashPath = keyof typeof PathToPageConfigLookup;

const createReverseLookup = <T extends Record<string, { id: string }>>(
  map: T
) =>
  Object.fromEntries(
    Object.entries(map).map(([key, value]) => [value.id, key as keyof T])
  ) as Record<string, keyof T>;

const PageIdToPathLookup = createReverseLookup(PathToPageConfigLookup);

/** Paths not covered by {@link PathToPageConfigLookup} (e.g. screener slugs). */
const pageIdToExplicitPath: Record<string, string> = {
  [companyScreener.id]: PagePaths.COMPANY_SCREENER,
  [geographyScreener.id]: PagePaths.GEOGRAPHY_SCREENER,
  [roleScreener.id]: PagePaths.ROLE_SCREENER,
  [talentDiscovery.id]: PagePaths.TALENT_DISCOVERY,
  [postingDiscovery.id]: PagePaths.POSTING_DISCOVERY,
};

export const getPathForDashboardId = (id: string): string => {
  if (PageIdToPathLookup[id]) {
    return '/' + PageIdToPathLookup[id];
  }
  const explicit = pageIdToExplicitPath[id];
  if (explicit) {
    return '/' + explicit;
  }

  return '';
};

export const getPageConfigForPath = (path: PublicCustomDashPath): Page =>
  PathToPageConfigLookup[path];
